import { isVolatile } from "@/__main__/constants.mjs";
import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, {
  DeepPartial,
  Mapped,
  Optional,
} from "@/__main__/data-model.mjs";
import { CONFIG } from "@/feature-ads/display-server-google.mjs";
import deepMerge from "@/util/deep-merge.mjs";
import { nullishMerge } from "@/util/optional-merge.mjs";
import { readCountryCode } from "@/util/use-country-code.mjs";

const videoProviderStruct = {
  threshold: Number,
  provider: String,
} satisfies ModelDefinition;

const configStruct = {
  enabled: Boolean,
  adhesion: Boolean,
  refresh: Number,
  maxRefreshCount: Number,
  mod: Number,
  bucket: Number,
  video: {
    refresh: Boolean,
  },
  videoEnabled: Boolean,
  videoEnabledWeb: Boolean,
  videoRefresh: Number,
  videoProviderExperiments: {
    providers: [videoProviderStruct],
  },
} satisfies ModelDefinition;

const defaultAdConfig: AdsConfig = {
  enabled: true,
  adhesion: false,
  refresh: 60,
  maxRefreshCount: CONFIG.ADS_REFRESH_COUNT_MAX,
  bucket: 0,
  mod: 1.5,
  video: {
    refresh: false,
  },
  videoEnabled: false,
  videoEnabledWeb: false,
  videoRefresh: 0,
  videoProviderExperiments: {
    providers: [
      {
        threshold: 0,
        provider: "exco",
      },
      {
        threshold: 0.1,
        provider: "vidazoo",
      },
      {
        threshold: 0.9,
        provider: "aditude",
      },
    ],
  },
};

// using DeepPartial on the model allows us to default on missing fields
const dataStruct = DeepPartial({
  ...configStruct,
  overrides: Optional(
    Mapped({
      key: String,
      value: DeepPartial(configStruct),
    }),
  ),
  videoProviderExperiments: {
    ...configStruct.videoProviderExperiments,
    countrySpecific: Optional([
      {
        countryCodes: [String],
        providers: [videoProviderStruct],
      },
    ]),
  },
});

// use fetchCountryCode at some point before this!
const AdsStatic = Object.assign(
  createModel(dataStruct).transform(configStruct, (config) => {
    applyOverrides(config);
    const configTransformed = nullishMerge(config, defaultAdConfig);
    configTransformed[isVolatile] = true;
    return configTransformed;
  }),
  {
    default: defaultAdConfig,
  },
);

export type AdsConfig = FromModel<typeof configStruct>;

function applyOverrides(config: FromModel<typeof dataStruct>) {
  const { overrides, videoProviderExperiments = {} } = config;
  const { countrySpecific } = videoProviderExperiments;
  const region = readCountryCode();
  delete config.overrides;
  delete config.videoProviderExperiments?.countrySpecific;

  if (overrides) {
    const regionOverride = overrides[`region-${region}`];

    if (regionOverride) deepMerge(config, regionOverride, false);
  }

  if (countrySpecific) {
    const videoProviderExperiment = countrySpecific.find((c) => {
      return c.countryCodes.includes(region);
    });

    if (videoProviderExperiment) {
      delete videoProviderExperiment.countryCodes;
      deepMerge(
        config.videoProviderExperiments,
        videoProviderExperiment,
        false,
      );
    }
  }
  return config;
}

export default AdsStatic;
